import React, { useState } from 'react';
import AppNav from '../components/app/AppNav';

const faqItems = [
  {
    q: 'How do I download Sahayy?',
    a: 'Sahayy is available for download on both iOS and Android devices. Search for "Sahayy" in the App Store or Google Play Store.',
  },
  {
    q: 'Is Sahayy free to use?',
    a: 'Yes, Sahayy is completely free to download and use. Core community features will always remain free.',
  },
  {
    q: 'How does Sahayy verify users?',
    a: 'We use email verification and community reporting to ensure users are real people within their claimed community.',
  },
  {
    q: 'Is my data secure with Sahayy?',
    a: 'Yes, we use industry-standard encryption and security practices to protect your personal information. See our Privacy Policy for details.',
  },
];

const Contact: React.FC = () => {
  const [formState, setFormState] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await fetch('https://api.sahayy.com/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormState({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 5000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <AppNav />

      {/* Hero */}
      <div className="bg-indigo-600 text-white px-6 py-16 text-center">
        <p className="text-indigo-200 text-xs font-semibold uppercase tracking-widest mb-3">Contact</p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">Get in Touch</h1>
        <p className="text-indigo-200 text-lg">We'd love to hear from you. Reach out through any of these channels.</p>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-14">
        {/* Contact details */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {[
            { label: 'Email', sub: 'For general inquiries', value: 'care@sahayy.com', href: 'mailto:care@sahayy.com' },
            { label: 'Phone', sub: 'Speak with our team', value: '+49 159 0648 9957', href: 'tel:+4915906489957' },
            { label: 'Office', sub: 'Visit us in Berlin', value: 'In den Alboingarten, 12103 Berlin', href: null },
          ].map((item, i) => (
            <div key={i} className="bg-gray-50 border border-gray-100 rounded-2xl px-5 py-5 text-center">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">{item.label}</h3>
              <p className="text-xs text-gray-400 mb-2">{item.sub}</p>
              {item.href ? (
                <a href={item.href} className="text-indigo-600 font-semibold text-sm hover:text-indigo-500 transition-colors">
                  {item.value}
                </a>
              ) : (
                <span className="text-indigo-600 font-semibold text-sm">{item.value}</span>
              )}
            </div>
          ))}
        </div>

        {/* Contact Form */}
        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-8 mb-12">
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-green-500 text-white flex items-center justify-center text-2xl mx-auto mb-4">✓</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
              <p className="text-gray-500">Thank you for contacting us. We'll get back to you shortly.</p>
            </div>
          ) : (
            <>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-1">Send us a message</h3>
              <p className="text-gray-400 text-sm text-center mb-7">We'll get back to you within 24 hours</p>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <input
                  type="text" name="name" placeholder="Full name"
                  value={formState.name} onChange={handleChange} required
                  className="px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 transition-colors"
                />
                <input
                  type="email" name="email" placeholder="Email address"
                  value={formState.email} onChange={handleChange} required
                  className="px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 transition-colors"
                />
                <input
                  type="text" name="subject" placeholder="Subject"
                  value={formState.subject} onChange={handleChange} required
                  className="px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 transition-colors"
                />
                <textarea
                  name="message" placeholder="Your message" rows={5}
                  value={formState.message} onChange={handleChange} required
                  className="px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 transition-colors resize-y"
                />
                <button
                  type="submit" disabled={isLoading}
                  className={`self-center px-8 py-3.5 rounded-2xl font-bold transition-colors ${isLoading ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-500'}`}
                >
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </>
          )}
        </div>

        {/* FAQ */}
        <div>
          <div className="text-center mb-8">
            <p className="text-indigo-600 text-xs font-semibold uppercase tracking-widest mb-2">FAQ</p>
            <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </div>
          <div className="flex flex-col gap-4">
            {faqItems.map((item, i) => (
              <div key={i} className="bg-gray-50 border border-gray-100 rounded-2xl px-6 py-5">
                <h3 className="font-bold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
